import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, FileText, AlertCircle, Calendar, DollarSign, Settings, Archive, History, User, ArrowUpDown, ChevronDown, ChevronUp, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePracticeStore } from '@/store/practiceStore';
import { usePracticeTypeStore } from '@/store/practiceTypeStore';
import { useClientStore } from '@/store/clientStore';
import { useAuthStore } from '@/store/authStore';
import { formatDate } from '@/utils/date';
import { toast } from 'sonner';

type ViewMode = 'active' | 'historical' | 'completed' | 'archived';

export default function PracticesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isArchivedPage = location.pathname.includes('/archived');
  const [viewMode, setViewMode] = useState<ViewMode>(isArchivedPage ? 'archived' : 'active');
  
  const {
    practices,
    loading,
    error,
    pagination,
    fetchPractices,
    setFilters,
    clearFilters,
    archivePractice,
  } = usePracticeStore();

  const {
    practiceTypes,
    fetchPracticeTypes,
  } = usePracticeTypeStore();

  const {
    clients,
    fetchClients,
  } = useClientStore();

  useEffect(() => {
    fetchPracticeTypes(false);
    fetchClients({ page: 1, limit: 1000 });
  }, [fetchPracticeTypes, fetchClients]);

  const buildFetchParams = (page: number, search?: string) => {
    const query = (search || '').trim();
    const params: any = {
      page,
      limit: itemsPerPage,
      viewMode: query ? 'all' : viewMode,
      sortBy,
      sortOrder,
    };
    if (query) params.search = query;
    return params;
  };

  const loadPractices = (page: number, search?: string) => {
    const query = (search || '').trim();
    setActiveQuery(query);
    fetchPractices(buildFetchParams(page, query));
  };

  useEffect(() => {
    if (isArchivedPage && viewMode !== 'archived') {
      setViewMode('archived');
      return;
    }
    setCurrentPage(1);
    clearFilters();
    loadPractices(1, searchTerm);
  }, [viewMode, sortBy, sortOrder, itemsPerPage]);

  useEffect(() => {
    if (isArchivedPage) {
      setViewMode('archived');
    }
  }, [location.pathname]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadPractices(1, searchTerm);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadPractices(page, searchTerm);
  };

  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === 'archived' && !isArchivedPage) {
      navigate('/practices/archived');
      return;
    }
    if (isArchivedPage && mode !== 'archived') {
      navigate('/practices');
    }
    setViewMode(mode);
    setSearchTerm('');
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const getDaysUntilDue = (dueDate: string | null | undefined): number | null => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getProgressPercentage = (practice: any): number => {
    if (practice.status === 'COMPLETED' || practice.status === 'ARCHIVED') return 100;
    const totalActivities = practice._count?.activities || 0;
    const completedActivities = practice._count?.activitiesCompleted || 0;
    if (totalActivities === 0) {
      if (practice.status === 'PENDING') return 0;
      if (practice.status === 'IN_PROGRESS') return 50;
      if (practice.status === 'SUSPENDED') return 25;
      return 0;
    }
    return Math.round((completedActivities / totalActivities) * 100);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      PENDING: 'secondary',
      IN_PROGRESS: 'default',
      SUSPENDED: 'outline',
      COMPLETED: 'default',
      ARCHIVED: 'outline',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      LOW: 'bg-gray-100 text-gray-800',
      MEDIUM: 'bg-blue-100 text-blue-800',
      HIGH: 'bg-orange-100 text-orange-800',
      URGENT: 'bg-red-100 text-red-800',
    };
    return (
      <Badge className={colors[priority] || 'bg-gray-100 text-gray-800'}>
        {priority}
      </Badge>
    );
  };

  const viewModeLabel: Record<ViewMode, string> = {
    active: 'Pratiche Attive',
    historical: 'Pratiche Storiche',
    completed: 'Completate',
    archived: 'Archiviate',
  };

  const getListBucket = (practice: { status: string; isHistorical?: boolean }): ViewMode | null => {
    if (practice.status === 'ARCHIVED') return 'archived';
    if (practice.isHistorical) return 'historical';
    if (practice.status === 'COMPLETED') return 'completed';
    if (practice.status === 'PENDING' || practice.status === 'IN_PROGRESS' || practice.status === 'SUSPENDED') {
      return 'active';
    }
    return null;
  };

  const bucketLabel: Record<ViewMode, string> = {
    active: 'Aperta',
    historical: 'Storica',
    completed: 'Completata',
    archived: 'Archiviata',
  };

  const bucketClass: Record<ViewMode, string> = {
    active: 'bg-emerald-100 text-emerald-800',
    historical: 'bg-amber-100 text-amber-800',
    completed: 'bg-sky-100 text-sky-800',
    archived: 'bg-gray-200 text-gray-700',
  };

  if (loading && !practices.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento pratiche...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pratiche</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestisci tutte le pratiche dello studio
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'ADMIN' && (
            <Button
              variant="outline"
              onClick={() => navigate('/practice-types')}
            >
              <Settings className="h-4 w-4 mr-2" />
              Tipi Pratiche
            </Button>
          )}
          <Button onClick={() => navigate('/practices/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Pratica
          </Button>
          <Button variant="outline" onClick={() => navigate('/practices/historical/new')}>
            <History className="h-4 w-4 mr-2" />
            Importa Storica
          </Button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-1 border-b">
        {(['active', 'historical', 'completed', 'archived'] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => handleViewModeChange(mode)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              viewMode === mode
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {viewModeLabel[mode]}
          </button>
        ))}
      </div>

      {/* Search + Sort */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                placeholder="Cerca in tutte le pratiche: titolo, cliente, descrizione..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Cerca
            </Button>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v)}>
              <SelectTrigger className="w-[200px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Ordina per..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Data inserimento</SelectItem>
                <SelectItem value="practiceNumber">Numero pratica</SelectItem>
                <SelectItem value="dueDate">Data scadenza</SelectItem>
                <SelectItem value="assignedTo">Operatore</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={toggleSortOrder} title={sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}>
              {sortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          {activeQuery && (
            <p className="mt-3 text-sm text-gray-500">
              Risultati in tutte le pratiche per «{activeQuery}»
            </p>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Practices List */}
      <div className="grid gap-4">
        {practices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nessuna pratica trovata</p>
            </CardContent>
          </Card>
        ) : (
          practices.map((practice) => {
            const daysUntilDue = getDaysUntilDue(practice.dueDate);
            const progress = getProgressPercentage(practice);
            const listBucket = activeQuery ? getListBucket(practice) : null;

            return (
              <Card key={practice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <Link to={`/practices/${practice.id}`} className="flex-1">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          {practice.practiceNumber && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-mono rounded">
                              #{practice.practiceNumber}
                            </span>
                          )}
                          <h3 className="text-lg font-semibold">{practice.title}</h3>
                          {listBucket && (
                            <Badge className={bucketClass[listBucket]}>
                              {bucketLabel[listBucket]}
                            </Badge>
                          )}
                          {getStatusBadge(practice.status)}
                          {getPriorityBadge(practice.priority)}
                          {practice.type && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              {practice.type.icon && <span>{practice.type.icon}</span>}
                              {practice.type.name}
                            </Badge>
                          )}
                          {(practice._count?.children ?? 0) > 0 && (
                            <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                              <GitBranch className="h-3 w-3" />
                              {practice._count!.children} sotto-pratic{practice._count!.children === 1 ? 'a' : 'he'}
                            </Badge>
                          )}
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-3 mb-4">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Progresso</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                progress === 100 ? 'bg-green-500' :
                                progress >= 50 ? 'bg-blue-500' :
                                progress > 0 ? 'bg-yellow-500' : 'bg-gray-300'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span>Tipo: {practice.type?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {(() => {
                              const isClosedPractice = practice.status === 'COMPLETED' || practice.status === 'ARCHIVED';
                              const calendarColor = isClosedPractice ? '' : (
                                daysUntilDue !== null && daysUntilDue < 0 ? 'text-red-500' :
                                daysUntilDue !== null && daysUntilDue < 7 ? 'text-orange-500' : ''
                              );
                              return <Calendar className={`h-4 w-4 ${calendarColor}`} />;
                            })()}
                            <span>
                              {practice.dueDate ? (
                                <>
                                  Scadenza: {formatDate(practice.dueDate)}
                                  {daysUntilDue !== null && (() => {
                                    const isClosedPractice = practice.status === 'COMPLETED' || practice.status === 'ARCHIVED';
                                    
                                    if (isClosedPractice) {
                                      if (practice.completedAt && practice.dueDate) {
                                        const completedDate = new Date(practice.completedAt);
                                        const dueDateObj = new Date(practice.dueDate);
                                        const wasLate = completedDate > dueDateObj;
                                        if (wasLate) {
                                          const daysLate = Math.ceil((completedDate.getTime() - dueDateObj.getTime()) / (1000 * 60 * 60 * 24));
                                          return <span className="ml-2 text-orange-600">(Chiusa con {daysLate} giorni di ritardo)</span>;
                                        }
                                        return <span className="ml-2 text-green-600">(Chiusa in tempo)</span>;
                                      }
                                      return null;
                                    }
                                    
                                    return (
                                      <span className={`ml-2 ${
                                        daysUntilDue < 0 ? 'text-red-600 font-bold' :
                                        daysUntilDue < 7 ? 'text-orange-600 font-semibold' : ''
                                      }`}>
                                        ({daysUntilDue < 0 ? `Scaduta ${Math.abs(daysUntilDue)} giorni fa` :
                                          daysUntilDue === 0 ? 'Scade oggi' :
                                          `Tra ${daysUntilDue} giorni`})
                                      </span>
                                    );
                                  })()}
                                </>
                              ) : (
                                'Nessuna scadenza'
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            <span>
                              Cliente: {practice.client?.firstName} {practice.client?.lastName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>
                              Operatore: {practice.assignedTo
                                ? `${practice.assignedTo.firstName} ${practice.assignedTo.lastName}`
                                : 'Non assegnato'}
                            </span>
                          </div>
                          {user?.canAccessQuotesAndAccounts && practice.amount && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4" />
                              <span>
                                Importo: €{Number(practice.amount).toFixed(2)}
                                {practice.isPaid && ' (Pagato)'}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Timeline mini */}
                        <div className="mt-4 pt-4 border-t flex items-center gap-4 text-xs text-gray-500">
                          <span>Iniziata: {formatDate(practice.startDate)}</span>
                          {practice.dueDate && (
                            <span>Scade: {formatDate(practice.dueDate)}</span>
                          )}
                          {practice.completedAt && (
                            <span className="text-green-600">Completata: {formatDate(practice.completedAt)}</span>
                          )}
                        </div>

                        {practice.description && (
                          <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                            {practice.description}
                          </p>
                        )}
                      </div>
                    </Link>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {practice.status === 'COMPLETED' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (window.confirm('Sei sicuro di voler archiviare questa pratica?')) {
                              try {
                                await archivePractice(practice.id);
                                toast.success('Pratica archiviata con successo');
                                loadPractices(currentPage, searchTerm);
                              } catch {
                                toast.error('Errore nell\'archiviazione della pratica');
                              }
                            }
                          }}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archivia
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total > 10 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              variant="outline"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage}
            >
              Precedente
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage}
            >
              Successiva
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-700">
                Mostrando{' '}
                <span className="font-medium">
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>{' '}
                a{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                di <span className="font-medium">{pagination.total}</span> risultati
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Elementi per pagina:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => setItemsPerPage(parseInt(value))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="rounded-l-md"
                >
                  Precedente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="rounded-r-md"
                >
                  Successiva
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
